// Item 4 — one selection engine shared by the standalone filter widgets and the
// per-widget "Filters on this visual" picker, so both behave identically:
// search never destroys hidden selections, Select all works on the visible rows only,
// and include/exclude maps straight onto the backend's in / not_in operators.

export const BLANK_TOKEN = "(Blank)";   // legacy: null OR empty/whitespace text
export const NULL_TOKEN = "(Null)";     // strictly missing values
export const EMPTY_TOKEN = "(Empty)";   // empty or whitespace-only text, never null

// Values the backend resolves with its own masks instead of a literal comparison.
export const SPECIAL_TOKENS = [NULL_TOKEN, EMPTY_TOKEN, BLANK_TOKEN];

// Human label for a raw value, keeping NULL / "" / "   " / 0 visually distinct.
export function valueLabel(v) {
  if (v === NULL_TOKEN) return "(null)";
  if (v === EMPTY_TOKEN) return "(empty text)";
  if (v === BLANK_TOKEN) return "(blank)";
  if (v === null || v === undefined) return "(null)";
  const s = String(v);
  if (s === "") return "(empty text)";
  if (s.trim() === "") return `(${s.length} space${s.length > 1 ? "s" : ""})`;
  return s;
}

export const isSpecial = (v) => SPECIAL_TOKENS.includes(v);

const key = (v) => String(v);

export function matchesSearch(v, q) {
  if (!q) return true;
  return valueLabel(v).toLowerCase().includes(q.toLowerCase());
}

// Select all = add every visible row, keep selections hidden by the search.
export function selectAllVisible(sel, visible) {
  const have = new Set(sel.map(key));
  return [...sel, ...visible.filter((v) => !have.has(key(v)))];
}

// Clear = drop the visible rows only (selections outside the search survive).
export function clearVisible(sel, visible) {
  const vis = new Set(visible.map(key));
  return sel.filter((v) => !vis.has(key(v)));
}

export function invertVisible(sel, visible) {
  const have = new Set(sel.map(key));
  const kept = clearVisible(sel, visible);
  return [...kept, ...visible.filter((v) => !have.has(key(v)))];
}

// "all" | "none" | "some" — drives the tri-state Select all checkbox.
export function visibleState(sel, visible) {
  if (!visible.length) return "none";
  const have = new Set(sel.map(key));
  const hits = visible.filter((v) => have.has(key(v))).length;
  if (!hits) return "none";
  return hits === visible.length ? "all" : "some";
}

// ---------------------------------------------------------------------------
// Value shape: a plain array (include) or { v: [...], exclude: true }.
// Both shapes are accepted everywhere so existing dashboards keep working.
// ---------------------------------------------------------------------------
export function readSelection(val) {
  if (val && !Array.isArray(val) && typeof val === "object") {
    return { values: Array.isArray(val.v) ? val.v : [], exclude: !!val.exclude };
  }
  return { values: Array.isArray(val) ? val : val != null && val !== "" ? [val] : [], exclude: false };
}

export function writeSelection(values, exclude) {
  if (!values.length) return exclude ? { v: [], exclude: true } : null;
  return exclude ? { v: values, exclude: true } : values;
}
